'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Lock, LogOut, Building2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useSubscription } from '@/hooks/useSubscription';
import { useGlobalOrganization } from '@/hooks/useGlobalOrganization';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { setServerLockedOrg } from '@/lib/subscriptionSignal';
import { apiService } from '@/lib/api';
import { useCheckoutReturn } from '@/hooks/useCheckoutReturn';
import { PlanPicker } from './PlanPicker';

/**
 * Shown instead of the app when the organization's subscription is locked (the read-only
 * grace period has elapsed). Nothing is lost — the data is retained and comes back as soon
 * as the subscription is renewed.
 */
export function SubscriptionLockedScreen({ onSignOut }: { onSignOut?: () => void }) {
    const { subscription } = useSubscription();
    const { organizations, selectedOrgId, setSelectedOrgId } = useGlobalOrganization();
    // Only an org admin can update billing; the API 403s a technician. Gate the CTA on the role so a
    // technician sees a "contact your admin" message instead of a button that fails when they tap it.
    const { isAdmin } = useCurrentUser();
    const wasTrial = subscription?.status === 'Trialing';
    // Returning from Stripe Checkout: activation is async, so poll until Active (then reload).
    const checkout = useCheckoutReturn();
    // A locked org whose stored status is still Active is a lapsed payment (grace elapsed), not a
    // trial-end or a deliberate cancel. It already has a plan, so the right action is to pay — retry
    // the outstanding invoice / update the card — not re-pick a plan.
    const isPaymentLapse = subscription?.status === 'Active';
    // Canceled (period over) still has a Stripe customer and usually a card; picking a plan charges
    // that card immediately, so they need the same "Update card" path as a payment lapse.
    const isCanceled = subscription?.status === 'Canceled';
    const canUpdateCard = isPaymentLapse || isCanceled;
    const [showPicker, setShowPicker] = useState(false);
    const [busy, setBusy] = useState<null | 'retry' | 'card'>(null);
    const [payMsg, setPayMsg] = useState<string | null>(null);
    // Native builds cannot sell subscriptions in-app (App Store IAP rules), so they keep the
    // contact-us CTA; the web app gets full self-service checkout.
    const isNative = Capacitor.isNativePlatform();

    // Card on file + unpaid-invoice hosted page. Needed for a lapsed payment (3-D Secure) and for a
    // canceled org picking a plan (so they can see/change the card that will be charged).
    const { data: billing } = useQuery({
        // Org-scoped: a user in multiple orgs must not see org A's card/invoices/hosted-pay URL cached
        // under org B after switching.
        queryKey: ['billing', selectedOrgId],
        queryFn: () => apiService.getBilling(),
        // Admins only: the hosted-invoice link is a payment surface; a technician has no billing CTA
        // here anyway (they see the "ask an admin" message).
        enabled: !isNative && canUpdateCard && isAdmin,
        staleTime: 30_000,
    });
    const hostedInvoiceUrl = billing?.invoices?.find(
        (i) => (i.status === 'PaymentFailed' || i.status === 'Invoiced') && i.hosted_invoice_url,
    )?.hosted_invoice_url ?? null;

    const retryPayment = async () => {
        setBusy('retry');
        setPayMsg(null);
        try {
            const r = await apiService.retryPayment();
            if (r.retried === 0) setPayMsg('No outstanding payment to retry.');
            else if (r.failed > 0) setPayMsg(r.error || 'The payment still did not go through. Update your card and try again.');
            else window.location.reload();
        } catch {
            setPayMsg('Could not retry the payment. Please try again.');
        } finally {
            setBusy(null);
        }
    };

    const updateCard = async () => {
        setBusy('card');
        setPayMsg(null);
        try {
            const { url } = await apiService.createBillingPortalSession();
            window.location.href = url;
        } catch {
            setPayMsg('Could not open the card update page. Please try again.');
            setBusy(null);
        }
    };

    // Native can't sell a NEW subscription in-app (App Store IAP), but recovering a LAPSED one — paying
    // an existing subscription's failed invoice — is not a new purchase, so it can happen on the web.
    // Open the web billing page in the system browser rather than dead-ending on "contact us".
    // The system browser needs an ABSOLUTE url; a relative one silently fails. VITE_APP_PUBLIC_URL is
    // set in every build env, but guard anyway so a misconfigured build falls back to the contact CTA.
    const webBillingBase = import.meta.env.VITE_APP_PUBLIC_URL as string | undefined;
    const canOpenWebBilling = !!webBillingBase && /^https?:\/\//.test(webBillingBase);
    const openWebBilling = async () => {
        if (!canOpenWebBilling) return;
        try {
            await Browser.open({ url: `${webBillingBase}/settings?tab=billing` });
        } catch {
            setPayMsg('Could not open the billing page. Please try again.');
        }
    };

    // A user can belong to several organizations, and the last one is auto-selected on every
    // login. Without this they would be stuck on the lock screen of one expired organization
    // and unable to reach the others.
    const otherOrganizations = organizations.filter(o => o.organization_id !== selectedOrgId);

    const switchOrganization = (orgId: string) => {
        // Clear the flag so the newly selected organization is judged on its own subscription.
        setServerLockedOrg(null);
        setSelectedOrgId(orgId);
    };

    return (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-gray-50 p-4 overflow-y-auto">
            <div className={`w-full ${showPicker ? 'max-w-3xl' : 'max-w-md'} my-8 rounded-2xl bg-white p-8 shadow-xl text-center`}>
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
                    <Lock className="h-7 w-7 text-amber-600" />
                </div>

                <h1 className="mb-2 text-xl font-semibold text-gray-900">
                    {wasTrial ? 'Your trial has ended' : isPaymentLapse ? 'Payment needed to continue' : 'Your subscription has ended'}
                </h1>

                <p className="mb-6 text-sm leading-relaxed text-gray-600">
                    {wasTrial
                        ? 'Choose a plan to get back into your account.'
                        : isPaymentLapse
                            ? "We couldn't process your last payment. Retry it or update your card to restore access."
                            : `Your ${subscription?.plan_name ?? ''} subscription is no longer active.`}
                    {' '}All of your data is safe and will be exactly where you left it.
                </p>

                {(checkout === 'processing' || checkout === 'timeout') && (
                    <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
                        {checkout === 'processing'
                            ? 'Payment received — activating your subscription…'
                            : 'Your payment is processing. This can take a moment — refresh shortly if it hasn\'t updated.'}
                    </div>
                )}

                {checkout === 'cancelled' && (
                    <div className="mb-4 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
                        Checkout was cancelled — you haven't been charged. You can try again whenever you're ready.
                    </div>
                )}

                {!isAdmin ? (
                    <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
                        {isPaymentLapse
                            ? 'A payment is needed to restore access. Please ask an administrator of this organization to update the billing details.'
                            : 'Please ask an administrator of this organization to choose a plan.'}
                    </div>
                ) : isNative ? (
                    // Native admin: a lapsed subscription can be paid on the web (not a new IAP purchase);
                    // a brand-new subscription still can't be sold in-app, so that keeps the contact CTA.
                    // Falls back to contact if we have no absolute web URL to open.
                    isPaymentLapse && canOpenWebBilling ? (
                        <button
                            onClick={openWebBilling}
                            className="block w-full rounded-xl bg-blue-600 px-4 py-3 font-medium text-white transition-colors hover:bg-blue-700"
                        >
                            Update payment
                        </button>
                    ) : (
                        <a
                            href="mailto:support@servicestrike.com?subject=Subscription"
                            className="block w-full rounded-xl bg-blue-600 px-4 py-3 font-medium text-white transition-colors hover:bg-blue-700"
                        >
                            Contact us to subscribe
                        </a>
                    )
                ) : showPicker ? (
                    <div className="space-y-3">
                        {canUpdateCard && billing?.card && (
                            <p className="text-sm text-gray-500">
                                Card on file:{' '}
                                <span className="capitalize">{billing.card.brand}</span>
                                {' '}•••• {billing.card.last4}. Selecting a plan charges this card.
                            </p>
                        )}
                        <PlanPicker currentPlanKey={null} onActivated={() => window.location.reload()} />
                        {canUpdateCard && (
                            <button
                                onClick={updateCard}
                                disabled={busy !== null}
                                className="block w-full rounded-xl border border-gray-300 px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                            >
                                {busy === 'card' ? 'Opening…' : 'Update card'}
                            </button>
                        )}
                        {payMsg && <p className="text-sm text-red-600">{payMsg}</p>}
                    </div>
                ) : isPaymentLapse ? (
                    <div className="space-y-2">
                        <button
                            onClick={retryPayment}
                            disabled={busy !== null}
                            className="block w-full rounded-xl bg-blue-600 px-4 py-3 font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                        >
                            {busy === 'retry' ? 'Retrying…' : 'Retry payment'}
                        </button>
                        <button
                            onClick={updateCard}
                            disabled={busy !== null}
                            className="block w-full rounded-xl border border-gray-300 px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                        >
                            Update card
                        </button>
                        {payMsg && <p className="text-sm text-red-600">{payMsg}</p>}
                        {hostedInvoiceUrl && (
                            <a
                                href={hostedInvoiceUrl}
                                className="block text-sm text-blue-600 underline hover:text-blue-700"
                            >
                                Card needs verification? Complete payment on our secure page
                            </a>
                        )}
                        <button
                            onClick={() => setShowPicker(true)}
                            className="mt-1 text-sm text-gray-500 underline hover:text-gray-700"
                        >
                            Or choose a different plan
                        </button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <button
                            onClick={() => setShowPicker(true)}
                            className="block w-full rounded-xl bg-blue-600 px-4 py-3 font-medium text-white transition-colors hover:bg-blue-700"
                        >
                            Choose a plan
                        </button>
                        {canUpdateCard && (
                            <>
                                {billing?.card && (
                                    <p className="text-sm text-gray-500">
                                        Card on file:{' '}
                                        <span className="capitalize">{billing.card.brand}</span>
                                        {' '}•••• {billing.card.last4}
                                    </p>
                                )}
                                <button
                                    onClick={updateCard}
                                    disabled={busy !== null}
                                    className="block w-full rounded-xl border border-gray-300 px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                                >
                                    {busy === 'card' ? 'Opening…' : 'Update card'}
                                </button>
                            </>
                        )}
                        {payMsg && <p className="text-sm text-red-600">{payMsg}</p>}
                    </div>
                )}

                {otherOrganizations.length > 0 && (
                    <div className="mt-5 border-t border-gray-200 pt-4 text-left">
                        <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gray-500">
                            <Building2 className="h-3.5 w-3.5" />
                            Switch to another organization
                        </p>
                        <div className="space-y-1.5">
                            {otherOrganizations.map(org => (
                                <button
                                    key={org.organization_id}
                                    onClick={() => switchOrganization(org.organization_id)}
                                    className="w-full truncate rounded-lg border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50"
                                >
                                    {org.organization_name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {onSignOut && (
                    <button
                        onClick={onSignOut}
                        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-medium text-gray-600 transition-colors hover:bg-gray-100"
                    >
                        <LogOut className="h-4 w-4" />
                        Sign out
                    </button>
                )}
            </div>
        </div>
    );
}

export default SubscriptionLockedScreen;
